import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import memberService from '../../services/memberService';
import ProfilePicture from '../../components/common/ProfilePicture';
import { alpha, useTheme } from '@mui/material/styles';
// Import Material UI components
import {
  Box, Grid, Typography, Paper, Button, IconButton, 
  TextField, MenuItem, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Avatar, Chip,
  Pagination, CircularProgress, InputAdornment, Alert,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Snackbar, Menu, Tooltip, Container, Select, FormControl, InputLabel
} from '@mui/material';
// Import Material UI icons
import {
  People as PeopleIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  DeleteOutline as DeleteIcon,
  Warning as WarningIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as CsvIcon,
  Badge as BadgeIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  Support as SupportIcon
} from '@mui/icons-material';

// Helper function to construct profile picture URL
const constructProfilePictureUrl = (profilePicturePath, baseUrl) => {
  if (!profilePicturePath) return null;
  
  try {
    // If it's already a full URL, return it
    if (profilePicturePath.startsWith('http')) {
      return profilePicturePath;
    }
    
    // Use provided baseUrl or try to get it from environment
    let apiBaseUrl = baseUrl || import.meta.env.VITE_API_URL || '';
    
    // Default fallback URL
    if (!apiBaseUrl) apiBaseUrl = 'http://localhost:5000';
    
    // Remove /api suffix if present for image URLs
    apiBaseUrl = apiBaseUrl.endsWith('/api') ? apiBaseUrl.slice(0, -4) : apiBaseUrl;
    
    // Remove trailing slash if present
    apiBaseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    
    // Ensure the path starts with a slash
    let normalizedPath = profilePicturePath;
    if (!normalizedPath.startsWith('/')) {
      normalizedPath = '/' + normalizedPath;
    }
    
    // Construct and return the full URL
    return `${apiBaseUrl}${normalizedPath}`;
  } catch (e) {
    console.error('Error constructing profile picture URL:', e);
    return null;
  }
};

const MembersListPage = () => {
  const navigate = useNavigate(); // Add this line to correctly initialize navigate
  const theme = useTheme();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [membershipTypeFilter, setMembershipTypeFilter] = useState('');
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Add state for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add state for snackbar notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Handle download menu state
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  
  // Function to download members list as CSV or PDF
  const handleDownload = async (format) => {
    try {
      // Close the menu
      setDownloadMenuAnchor(null);
      
      // Get the API URL from environment variables or use default
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      
      // Get tenant ID
      const tenantId = localStorage.getItem('tenantId');
      
      if (!tenantId) {
        throw new Error('Tenant ID not found. Please log in again.');
      }
      
      // Build URL with query parameters matching current filters
      let url = `${apiUrl}/members/download/${format}?tenantId=${tenantId}`;
      
      // Add any active filters to the download request
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
      if (membershipTypeFilter) url += `&membershipType=${encodeURIComponent(membershipTypeFilter)}`;
      
      // Show loading notification
      setSnackbar({
        open: true,
        message: `Preparing ${format.toUpperCase()} download...`,
        severity: 'info'
      });
      
      // Trigger file download
      window.location.href = url;
      
      // Show success notification after a delay
      setTimeout(() => {
        setSnackbar({
          open: true,
          message: `${format.toUpperCase()} download started`,
          severity: 'success'
        });
      }, 1500);
    } catch (error) {
      console.error(`Error downloading ${format}:`, error);
      setSnackbar({
        open: true,
        message: `Failed to download ${format}: ${error.message}`,
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get the tenant ID - this needs to be added to headers and filters
        const tenantId = localStorage.getItem('tenantId');
        
        if (!tenantId) {
          setError('Tenant ID not found. Please log in again.');
          setIsLoading(false);
          return;
        }
        
        // Log search parameters for debugging
        console.log(`Search query: "${search}", Status filter: "${statusFilter}", Membership type: "${membershipTypeFilter}"`);
        console.log(`Fetching members page ${currentPage}, items per page: ${itemsPerPage} for tenant ${tenantId}`);
        
        // Create filters object with tenantId
        const filters = {
          tenant: tenantId
        };
        if (statusFilter) filters.status = statusFilter;
        if (membershipTypeFilter) filters.membershipType = membershipTypeFilter;
        
        // Add an X-Tenant-ID header to ensure proper tenant filtering on backend
        const authToken = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${authToken}`,
          'X-Tenant-ID': tenantId
        };
        
        const response = await memberService.getAllMembersWithHeaders(
          currentPage,
          itemsPerPage,
          search,
          filters,
          headers
        );

        if (!response) {
          throw new Error('No response from server');
        }

        console.log('API response:', response);

        // Handle different API response formats
        let memberData = [];
        let totalCount = 0;
        let paginationInfo = null;
        
        if (response.data && Array.isArray(response.data)) {
          memberData = response.data;
          paginationInfo = response.pagination;
          totalCount = paginationInfo?.total || memberData.length;
        } else if (Array.isArray(response)) {
          memberData = response;
          totalCount = memberData.length;
        } else if (response.success && response.data && Array.isArray(response.data)) {
          memberData = response.data;
          paginationInfo = response.pagination;
          totalCount = paginationInfo?.total || memberData.length;
        }
        
        // Safety check: ensure we only show members from the current tenant
        memberData = memberData.filter(member => {
          // Check for tenantId in various possible locations
          const memberTenantId = member.tenantId || 
                               (member.tenant && (typeof member.tenant === 'string' ? member.tenant : member.tenant._id));
          
          // If we can't determine the tenantId, don't show the member
          if (!memberTenantId) return false;
          
          // Check if the member belongs to the current tenant
          return memberTenantId === tenantId;
        });
        
        console.log(`Filtered ${memberData.length} members for tenant ${tenantId}`);
        setMembers(memberData);
        
        // Calculate total pages based on total count and items per page
        const calculatedPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));
        setTotalPages(calculatedPages);
        
        // If we got no results but we know there should be more pages, go back to page 1
        if (memberData.length === 0 && currentPage > 1 && calculatedPages >= 1) {
          console.log('No results on current page, returning to page 1');
          setCurrentPage(1);
        }
        
        // Extract membership types for filter dropdown
        if (memberData?.length > 0) {
          const types = [...new Set(memberData.map(m => m.membershipType).filter(Boolean))];
          if (types.length > 0) {
            setMembershipTypes(types);
          }
        }
      } catch (error) {
        console.error('Error fetching members:', error);
        setError(`Failed to load members: ${error.message || 'Unknown error'}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMembers();
  }, [currentPage, search, statusFilter, membershipTypeFilter, itemsPerPage]);

  // Handle page change
  const handlePageChange = (event, page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (event) => {
    const newValue = parseInt(event.target.value, 10);
    setItemsPerPage(newValue);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Handle reset filters
  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setMembershipTypeFilter('');
    setCurrentPage(1);
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (member) => {
    setMemberToDelete(member);
    setDeleteDialogOpen(true);
  };
  
  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setMemberToDelete(null);
  };

  // Handle member delete
  const handleDelete = async () => {
    if (!memberToDelete) return;
    
    try {
      setIsDeleting(true);
      const id = memberToDelete._id;
      
      await memberService.deleteMember(id);
      
      // Refresh the list with current page
      const updatedMembers = members.filter(member => member._id !== id);
      setMembers(updatedMembers);
      
      // If the page becomes empty and it's not the first page, go to previous page
      if (updatedMembers.length === 0 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
      
      // Show success notification
      setSnackbar({
        open: true,
        message: 'Member deleted successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting member:', error);
      
      // Show error notification
      setSnackbar({
        open: true,
        message: `Failed to delete member: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setIsDeleting(false);
      closeDeleteDialog();
    }
  };
  
  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({...snackbar, open: false});
  };

  const renderMemberList = () => {
    return (
      <Paper sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Membership</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => {
                const userRole = member.userId?.role || member.role || 'member';
                
                const getRoleIcon = (role) => {
                  switch(role) {
                    case 'admin':
                      return <AdminIcon color="primary" fontSize="small" />;
                    case 'staff':
                      return <SupportIcon color="secondary" fontSize="small" />;
                    case 'superadmin':
                      return <AdminIcon color="error" fontSize="small" />;
                    default:
                      return <PersonIcon fontSize="small" color="action" />;
                  }
                };
                
                const getRoleColor = (role) => {
                  switch(role) {
                    case 'admin':
                      return theme.palette.primary.main;
                    case 'staff':
                      return theme.palette.secondary.main;
                    case 'superadmin':
                      return theme.palette.error.main;
                    default:
                      return theme.palette.text.secondary;
                  }
                };

                return (
                  <TableRow 
                    key={member._id}
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                      '&:hover': { bgcolor: 'rgba(0,150,136,0.08)' },
                      ...(userRole === 'staff' && {
                        bgcolor: alpha(theme.palette.secondary.light, 0.07),
                        '&:hover': {
                          bgcolor: alpha(theme.palette.secondary.light, 0.14),
                        },
                        borderLeft: `4px solid ${theme.palette.secondary.main}`
                      })
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>
                          <ProfilePicture 
                            src={constructProfilePictureUrl(member.userId?.profilePicture || member.profilePicture)}
                            firstName={member.userId?.firstName || member.firstName}
                            lastName={member.userId?.lastName || member.lastName}
                            size="md"
                          />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            {member.userId?.firstName || member.firstName} {member.userId?.lastName || member.lastName}
                            {userRole === 'staff' && (
                              <Chip
                                icon={<SupportIcon fontSize="small" />}
                                label="Staff"
                                size="small"
                                sx={{ 
                                  ml: 1, 
                                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                  color: theme.palette.secondary.main,
                                  fontWeight: 'bold',
                                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                                }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.userId?.email || member.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{member.membershipNumber}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {member.membershipType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={member.membershipType || 'Regular'} 
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRoleIcon(userRole)}
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: getRoleColor(userRole),
                            fontWeight: userRole !== 'member' ? 'bold' : 'normal',
                            textTransform: 'capitalize'
                          }}
                        >
                          {userRole}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={member.status}
                        size="small"
                        color={
                          member.status === 'active' ? 'success' :
                          member.status === 'pending' ? 'warning' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={Link}
                        to={`/admin/members/${member._id}`}
                        color="primary"
                        size="small"
                        sx={{ mr: 1 }}
                      >
                        View
                      </Button>
                      <Button
                        onClick={() => openDeleteDialog(member)}
                        color="error"
                        size="small"
                        startIcon={<DeleteIcon />}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              Showing page {currentPage} of {totalPages}
            </Typography>
            <Pagination 
              count={totalPages} 
              page={currentPage} 
              onChange={handlePageChange} 
              color="primary"
              shape="rounded"
            />
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        mt: { xs: 1, md: 4 }, 
        mb: 4,
        px: { xs: 2, md: 3 }
      }}
    >
      {/* Page Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4,  
          p: 3, 
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
            <PeopleIcon sx={{ mr: 1 }} /> Members
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.8 }}>
            Manage your organization's membership
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/members/add')}
          >
            Add Member
          </Button>
          <Button
            variant="contained"
            sx={{ color: 'white', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
            startIcon={<CloudUploadIcon />}
            onClick={() => navigate('/admin/members/bulk-upload')}
          >
            Bulk Upload
          </Button>
          
          {/* Download Dropdown Button */}
          <Button
            variant="contained"
            sx={{ color: 'white', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
            startIcon={<DownloadIcon />}
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            disabled={members.length === 0}
          >
            Download
          </Button>
          
          {/* Download Menu */}
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
          >
            <MenuItem onClick={() => handleDownload('csv')}>
              <CsvIcon fontSize="small" sx={{ mr: 1 }} />
              Download as CSV
            </MenuItem>
            <MenuItem onClick={() => handleDownload('pdf')}>
              <PdfIcon fontSize="small" sx={{ mr: 1 }} />
              Download as PDF
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Filters and Search */}
      <Paper
        elevation={2}
        sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: 2,
          borderTop: '4px solid #2196F3'
        }}
      >
        <Grid container spacing={3}>
          {/* Search */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search"
              variant="outlined"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              placeholder="Search by name, email, or number"
            />
          </Grid>

          {/* Status Filter */}
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Status"
              variant="outlined"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </TextField>
          </Grid>

          {/* Membership Type Filter */}
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label="Membership Type"
              variant="outlined"
              value={membershipTypeFilter}
              onChange={(e) => setMembershipTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {membershipTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        {/* Reset Filters Button */}
        {(search || statusFilter || membershipTypeFilter) && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleResetFilters}
              startIcon={<RefreshIcon />}
              color="primary"
            >
              Reset filters
            </Button>
          </Box>
        )}
      </Paper>

      {/* Members Table */}
      {renderMemberList()}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle 
          sx={{
            bgcolor: 'error.light',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2
          }}
        >
          <WarningIcon color="inherit" /> Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1, px: 3, mt: 2 }}>
          <DialogContentText>
            Are you sure you want to delete the member{' '}
            <Typography 
              component="span" 
              fontWeight="bold" 
              color="text.primary"
              display="inline"
            >
              {memberToDelete?.userId?.firstName} {memberToDelete?.userId?.lastName}
            </Typography>
            ? This action cannot be undone.
          </DialogContentText>
          
          {memberToDelete && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mt: 3, 
              p: 2, 
              borderRadius: 1,
              bgcolor: 'rgba(0,0,0,0.02)',
              border: '1px solid rgba(0,0,0,0.08)'
            }}>
              {/* Check for profile picture */}
              {memberToDelete.userId?.profilePicture || memberToDelete.profilePicture ? (
                <Avatar sx={{ mr: 2 }}>
                  <ProfilePicture 
                    src={constructProfilePictureUrl(memberToDelete.userId?.profilePicture || memberToDelete.profilePicture)}
                    firstName={memberToDelete.userId?.firstName}
                    lastName={memberToDelete.userId?.lastName}
                    size="md"
                  />
                </Avatar>
              ) : (
                <Avatar 
                  sx={{ 
                    bgcolor: 'error.main', 
                    mr: 2 
                  }}
                >
                  {memberToDelete.userId?.firstName?.[0]}{memberToDelete.userId?.lastName?.[0]}
                </Avatar>
              )}
              <Box>
                <Typography variant="subtitle1">
                  {memberToDelete.userId?.firstName} {memberToDelete.userId?.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {memberToDelete.userId?.email}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Chip 
                    label={memberToDelete.status}
                    size="small"
                    color={
                      memberToDelete.status === 'active' ? 'success' :
                      memberToDelete.status === 'pending' ? 'warning' : 'default'
                    }
                    sx={{ mr: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Member since {new Date(memberToDelete.joinDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={closeDeleteDialog}
            variant="outlined"
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{ 
              bgcolor: 'error.main',
              '&:hover': {
                bgcolor: 'error.dark',
              }
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Member'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: 3,
            borderRadius: 2
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default MembersListPage;